import React, { useEffect, useCallback, useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { useTheme } from '@/hooks/useTheme';
import { TitleText, BodyText, Pagination, Button, FileUpload, ReferralStatusBadge } from '../ui';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { MedicalConditionTags } from '@/components/ui/MedicalConditionTags';
import { useReferralDocuments } from '@/hooks/useReferralDocuments';
import { apiService } from '@/services/ApiService';
import type { Referral } from '@/services/domains/ReferralDomain';
import { getLogger } from '@/services/shared/LoggerFactory';
import { BackButton } from '../ui/BackButton';
import { PhoneUtil } from '@/utils/PhoneUtil';

interface ReferralDetailsPageProps {
  referralId: string;
  className?: string;
}

export const ReferralDetailsPage: React.FC<ReferralDetailsPageProps> = ({
  referralId,
  className,
}) => {
  const logger = getLogger();
  const { isDark } = useTheme();

  // Referral data state
  const [referral, setReferral] = useState<Referral | null>(null);
  const [referralLoading, setReferralLoading] = useState(true);
  const [referralError, setReferralError] = useState<string | null>(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    documentId: string;
    filename: string;
  }>({
    isOpen: false,
    documentId: '',
    filename: ''
  });

  // Use custom hook for document management
  const {
    documents,
    loading: documentsLoading,
    error: documentsError,
    pagination,
    uploadProgress,
    fetchDocuments,
    downloadDocument,
    uploadDocument,
    deleteDocument,
    validateFile,
    retryFetch,
  } = useReferralDocuments();

  // Extract pagination values for easier access
  const { page: currentPage, totalPages, total: totalDocuments, perPage } = pagination;

  // Handle page change
  const handlePageChange = (page: number) => {
    fetchDocuments(referralId, page);
  };

  // Handle document download
  const handleDownloadDocument = (documentId: string, filename: string) => {
    downloadDocument(documentId, filename);
  };

  // Handle document deletion with confirmation
  const handleDeleteDocument = (documentId: string, filename: string) => {
    setConfirmDialog({
      isOpen: true,
      documentId,
      filename
    });
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    const { documentId } = confirmDialog;
    setConfirmDialog({ isOpen: false, documentId: '', filename: '' });

    const success = await deleteDocument(documentId);
    if (!success) {
      // Error handling is already done in the hook, but we could add a toast notification here
      logger.error('Failed to delete document');
    }
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setConfirmDialog({ isOpen: false, documentId: '', filename: '' });
  };

  // Handle file upload - wrapper to match FileUpload component interface
  const handleFileUpload = useCallback(async (file: File, description?: string) => {
    return await uploadDocument(referralId, file, { description });
  }, [uploadDocument, referralId]);

  // Format file size
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // Extract file extension from filename
  const getFileExtension = useCallback((filename: string): string => {
    if (!filename) return 'FILE';

    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
      return 'FILE'; // No extension or dot at the end
    }

    const extension = filename.substring(lastDotIndex + 1);
    return extension.toUpperCase();
  }, []);

  // Format date
  const formatDate = useCallback((dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Fetch referral data
  const fetchReferralData = useCallback(async () => {
    try {
      setReferralLoading(true);
      setReferralError(null);
      const referralData = await apiService.referral.getReferral(referralId);
      setReferral(referralData);
    } catch (error) {
      logger.error('Failed to fetch referral:', error);
      setReferralError(error instanceof Error ? error.message : 'Failed to load referral details');
    } finally {
      setReferralLoading(false);
    }
  }, [referralId]);

  // Set page title
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const title = referral
        ? `Referral Details - ${referral.patient_name || referralId} - BiteWise Healthcare Platform`
        : `Referral Details - ${referralId} - BiteWise Healthcare Platform`;
      document.title = title;
    }
  }, [referralId, referral]);

  // Fetch referral and documents when component mounts
  useEffect(() => {
    fetchReferralData();
    fetchDocuments(referralId, 1);
  }, [fetchReferralData, fetchDocuments, referralId]);

  return (
    <Layout className={className}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="referral-details">
        <div className="flex items-center gap-4 mb-4">
          <BackButton fallback='/dashboard'/>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div>
            <TitleText>
              Referral Details
            </TitleText>
            <p className={`text-base mt-2 ${isDark ? 'text-gray-400' : 'text-secondary-600'}`}>
              View and manage referral information
            </p>
          </div>
        </div>

        {/* Referral Information Card */}
        <div className={`rounded-lg border p-6 ${
          isDark
            ? 'bg-secondary-800 border-secondary-700'
            : 'bg-white border-secondary-200'
        }`}>
          {referralLoading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <BodyText className="mt-4">Loading referral details...</BodyText>
            </div>
          ) : referralError ? (
            <div className="p-8 text-center">
              <div className={`text-error-600 mb-4`}>
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <BodyText className="text-error-600 mb-4">{referralError}</BodyText>
              <Button
                onClick={fetchReferralData}
                variant="outline"
                size="sm"
              >
                Try Again
              </Button>
            </div>
          ) : referral ? (
            <div className="space-y-4">
              <div>
              {referral.patient_name && (
                <div>
                  <BodyText variant="secondary" size="sm" className="font-medium mb-1">
                    Patient Name
                  </BodyText>
                  <BodyText variant="muted" size="sm">
                    {referral.patient_name}
                  </BodyText>
                </div>
              )}

                <BodyText variant="secondary" size="sm" className="font-medium mb-1">
                  Status
                </BodyText>
                <ReferralStatusBadge status={referral.status} />
              </div>

              {referral.phone && (
                <div>
                  <BodyText variant="secondary" size="sm" className="font-medium mb-1">
                    Phone
                  </BodyText>
                  <BodyText variant="muted" size="sm">
                    {PhoneUtil.formatAustralian(referral.phone)}
                  </BodyText>
                </div>
              )}

              {referral.referring_physician_name && (
                <div>
                  <BodyText variant="secondary" size="sm" className="font-medium mb-1">
                    Referring Physician
                  </BodyText>
                  <BodyText variant="muted" size="sm">
                    {referral.referring_physician_role === 'nurse' ? '': 'Dr. '}{referral.referring_physician_name}
                  </BodyText>
                </div>
              )}

              {referral.conditions && referral.conditions.length > 0 && (
                <div>
                  <BodyText variant="secondary" size="sm" className="font-medium mb-1">
                    Health Conditions
                  </BodyText>
                  <MedicalConditionTags conditions={referral.conditions} variant="default" />
                </div>
              )}

              {referral.additional_notes && (
                <div>
                  <BodyText variant="secondary" size="sm" className="font-medium mb-1">
                    Additional Notes
                  </BodyText>
                  <BodyText variant="muted" size="sm">
                    {referral.additional_notes}
                  </BodyText>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Uploaded Documents Section */}
        <div className="mt-8">
          <div className="mb-6">
            <TitleText className="text-xl">
              Uploaded Documents
            </TitleText>
            <BodyText variant="secondary" className="mt-2">
              View and download documents uploaded for this referral
            </BodyText>
          </div>


          <div className={`rounded-lg border ${
            isDark
              ? 'bg-secondary-800 border-secondary-700'
              : 'bg-white border-secondary-200'
          }`}>
            {documentsLoading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <BodyText className="mt-4">Loading documents...</BodyText>
              </div>
            ) : documentsError ? (
              <div className="p-8 text-center">
                <div className={`text-error-600 mb-4`}>
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <BodyText className="text-error-600 mb-4">{documentsError}</BodyText>
                <Button
                  onClick={() => retryFetch(referralId)}
                  variant="outline"
                  size="sm"
                >
                  Try Again
                </Button>
              </div>
            ) : documents.length === 0 ? (
              <div className="p-8 text-center">
                <div className={`mb-4 ${isDark ? 'text-secondary-300' : 'text-secondary-600'}`}>
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <BodyText variant="secondary">No documents uploaded yet</BodyText>
              </div>
            ) : (
              <>
                {/* Documents Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
                    <thead className={isDark ? 'bg-secondary-900' : 'bg-secondary-50'}>
                      <tr>
                        <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? 'text-secondary-300' : 'text-secondary-500'
                        }`}>
                          File Name
                        </th>
                        <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? 'text-secondary-300' : 'text-secondary-500'
                        }`}>
                          Type
                        </th>
                        <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? 'text-secondary-300' : 'text-secondary-500'
                        }`}>
                          Size
                        </th>
                        <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? 'text-secondary-300' : 'text-secondary-500'
                        }`}>
                          Uploaded
                        </th>
                        <th scope="col" className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? 'text-secondary-300' : 'text-secondary-500'
                        }`}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-secondary-700' : 'divide-secondary-200'}`}>
                      {documents.map((doc) => (
                        <tr key={doc.id} className={isDark ? 'hover:bg-secondary-700' : 'hover:bg-secondary-50'}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                                isDark ? 'bg-primary-900 text-primary-200' : 'bg-primary-100 text-primary-800'
                              }`}>
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <BodyText className="font-medium">
                                  {doc.original_filename}
                                </BodyText>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              isDark
                                ? 'bg-secondary-700 text-secondary-200'
                                : 'bg-secondary-100 text-secondary-800'
                            }`}>
                              {getFileExtension(doc.original_filename)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <BodyText variant="secondary">
                              {formatFileSize(doc.file_size)}
                            </BodyText>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <BodyText variant="secondary">
                              {formatDate(doc.created_at)}
                            </BodyText>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                onClick={() => handleDownloadDocument(doc.id, doc.original_filename)}
                                variant="outline"
                                size="sm"
                                className="inline-flex items-center"
                              >
                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download
                              </Button>
                              <Button
                                onClick={() => handleDeleteDocument(doc.id, doc.original_filename)}
                                variant="outline"
                                size="sm"
                                className={`inline-flex items-center ${
                                  isDark
                                    ? 'text-error-400 border-error-600 hover:bg-error-900 hover:text-error-300'
                                    : 'text-error-600 border-error-300 hover:bg-error-50 hover:text-error-700'
                                }`}
                                disabled={documentsLoading}
                              >
                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-secondary-200 dark:border-secondary-700">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 flex justify-between sm:hidden">
                        <Button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage <= 1}
                          variant="outline"
                          size="sm"
                        >
                          Previous
                        </Button>
                        <Button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                          variant="outline"
                          size="sm"
                        >
                          Next
                        </Button>
                      </div>

                      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-center">
                        <div>
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={totalDocuments}
                            itemsPerPage={perPage}
                            onPageChange={handlePageChange}
                            showPageNumbers={true}
                            maxPageNumbers={5}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* File Upload Component */}
        <div className="mt-8 mb-6">

          <FileUpload
            onUpload={handleFileUpload}
            onValidate={validateFile}
            uploadProgress={uploadProgress}
            acceptedTypes={['.pdf', '.doc', '.docx']}
            maxSize={10 * 1024 * 1024} // 10MB
            disabled={documentsLoading}
            uploadButtonText="Upload Document"
            className="w-full"
          />
        </div>

      </div>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Document"
        message={`Are you sure you want to delete "${confirmDialog.filename}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        destructive={true}
        isLoading={documentsLoading}
      />
    </Layout>
  );
};

export default ReferralDetailsPage;